import { BaseEntity } from '@ianesthesie-kit/services';
import { GroupedList } from '@/util';
import { SourceType } from '@/modules/surgicalEvent';

export enum LabTestType {
  BLOODWORK = 'BLOODWORK',
  PULMONARY = 'PULMONARY',
  CARDIAC_ECHO = 'CARDIAC_ECHO',
  EKG = 'EKG',
  CORONARY_ANGIOGRAPHY = 'CORONARY_ANGIOGRAPHY',
  CAROTID_DOPPLER = 'CAROTID_DOPPLER',
  URINALYSIS = 'URINALYSIS',
  NASAL_CYTOBACTERIOLOGICAL = 'NASAL_CYTOBACTERIOLOGICAL',
  CT_ANGIOGRAPHY = 'CT_ANGIOGRAPHY',
  CARDIAC_MRI = 'CARDIAC_MRI',
  HOLTER_MONITOR = 'HOLTER_MONITOR',
  CHEST_XRAY = 'CHEST_XRAY'
}

interface BloodworkResults {
  hemoglobin?: number;
  platelets?: number;
  mcv?: number;
  proTime?: number;
  acTime?: number;
  inr?: number;
  potassium?: number;
  creatinine?: number;
  clearance?: number;
  hbA1C?: number;
  albumin?: number;
  ferritin?: number;
  tsat?: number;
}

interface CardiacEchoResults {
  lvef?: number;
  rightFunction?: string;
  leftFunction?: string;
  paps?: string;
}

interface PurlmonaryResults {
  fev1?: number;
  fvc?: number;
  fev1ToFvc?: number;
  dlco?: number;
}

type SourceTypeMap<TResults> = Record<keyof TResults, SourceType>;

interface LabTestResponseBase extends BaseEntity {
  type: LabTestType;
  date?: string;
  comment?: string;
  results?: CardiacEchoResults | BloodworkResults | PurlmonaryResults;
  sourceTypeMap?:
    | SourceTypeMap<CardiacEchoResults>
    | SourceTypeMap<BloodworkResults>
    | SourceTypeMap<PurlmonaryResults>;
}

export interface BloodworkLabTestResponse extends LabTestResponseBase {
  type: LabTestType.BLOODWORK;
  results?: BloodworkResults;
  sourceTypeMap?: SourceTypeMap<BloodworkResults>;
}

export interface CardiacEchoLabTestResponse extends LabTestResponseBase {
  type: LabTestType.CARDIAC_ECHO;
  results?: CardiacEchoResults;
  sourceTypeMap?: SourceTypeMap<CardiacEchoResults>;
}

export interface PulmonaryLabTestResponse extends LabTestResponseBase {
  type: LabTestType.PULMONARY;
  results?: PurlmonaryResults;
  sourceTypeMap?: SourceTypeMap<PurlmonaryResults>;
}

export interface AdditionalExamLabTestResponse extends BaseEntity {
  type: Exclude<
    LabTestType,
    LabTestType.BLOODWORK | LabTestType.CARDIAC_ECHO | LabTestType.PULMONARY
  >;
  date?: string;
  comment?: string;
}

export type LabTestResponse =
  | BloodworkLabTestResponse
  | CardiacEchoLabTestResponse
  | PulmonaryLabTestResponse
  | AdditionalExamLabTestResponse;

type LabTestBase<
  T extends LabTestType,
  TResults extends CardiacEchoResults | BloodworkResults | PurlmonaryResults
> = BaseEntity &
  TResults & {
    type: T;
    date?: string;
    comment?: string;
    sourceTypeMap?: SourceTypeMap<TResults>;
  };

export type BloodworkLabTest = LabTestBase<
  LabTestType.BLOODWORK,
  BloodworkResults
>;

export type CardiacEchoLabTest = LabTestBase<
  LabTestType.CARDIAC_ECHO,
  CardiacEchoResults
>;

export type PulmonaryLabTest = LabTestBase<
  LabTestType.PULMONARY,
  PurlmonaryResults
>;

export interface AdditionalExamLabTest extends BaseEntity {
  type: Exclude<
    LabTestType,
    LabTestType.BLOODWORK | LabTestType.CARDIAC_ECHO | LabTestType.PULMONARY
  >;
  date?: string;
  comment?: string;
}

export const ADDITIONAL_EXAM_TYPES = [
  LabTestType.EKG,
  LabTestType.CORONARY_ANGIOGRAPHY,
  LabTestType.CAROTID_DOPPLER,
  LabTestType.URINALYSIS,
  LabTestType.NASAL_CYTOBACTERIOLOGICAL,
  LabTestType.CT_ANGIOGRAPHY,
  LabTestType.CARDIAC_MRI,
  LabTestType.HOLTER_MONITOR,
  LabTestType.CHEST_XRAY
] as const satisfies ReadonlyArray<
  Exclude<
    LabTestType,
    LabTestType.BLOODWORK | LabTestType.CARDIAC_ECHO | LabTestType.PULMONARY
  >
>;

export type LabTest =
  | BloodworkLabTest
  | CardiacEchoLabTest
  | PulmonaryLabTest
  | AdditionalExamLabTest;

export type GroupedLabTests = GroupedList<LabTest, LabTestType, true>;
